import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Circle, Plus, ArrowRight, Gauge, ListChecks, TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

const BAND_STYLE = {
  excellent: "text-green-700", good: "text-emerald-700", needs_attention: "text-amber-700", critical: "text-red-700",
};
const BAND_LABEL = { excellent: "Excellent", good: "Good", needs_attention: "Needs Attention", critical: "Critical" };
const SEV_STYLE = { low: "bg-gray-100 text-gray-700", medium: "bg-amber-100 text-amber-800", high: "bg-orange-100 text-orange-800", critical: "bg-red-100 text-red-800" };
const ISSUE_TYPES = ["trainer_certification", "missing_attendance", "missing_homework", "dog_profile_incomplete", "dog_without_trainer", "class_without_trainer", "low_attendance", "method_compliance", "client_complaint", "audit_finding"];
const empty = { title: "", type: "method_compliance", severity: "medium", location_id: "", description: "", recommended_action: "" };

export default function Quality() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(user?.role);
  const [data, setData] = useState(null);
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/quality/dashboard"); setData(d.data);
      const i = await api.get("/quality/issues"); setIssues(i.data);
    } catch { setErr(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (chk) => { try { await api.post("/quality/issues/generate", chk); toast.success("Issue created & tracked"); await load(); } catch { toast.error("Failed"); } };
  const create = async () => { try { await api.post("/quality/issues", form); toast.success("Issue created"); setOpen(false); setForm(empty); await load(); } catch { toast.error("Failed"); } };

  if (err) return <div className="text-center py-20 text-muted-foreground" data-testid="quality-no-access">You don't have access to HV Quality.</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  if (data.scope === "trainer") return <TrainerQuality data={data} issues={issues} navigate={navigate} />;

  const byStatus = (s) => issues.filter((i) => i.status === s);

  return (
    <div>
      <PageHeader
        title="HV Quality"
        subtitle="Monitor how locations, trainers and classes follow the Hundeverket method — and help them improve."
        action={canManage && <Button onClick={() => { setForm(empty); setOpen(true); }} className="gap-2" data-testid="quality-add-issue"><Plus size={16} strokeWidth={1.5} /> New Issue</Button>}
      />

      {/* Score hero */}
      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-border lg:col-span-1 bg-neutral-900 text-primary-foreground">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold opacity-80"><Gauge size={14} /> {data.scope === "network" ? "Network" : "Location"} Quality Score</div>
            <div className="text-5xl font-heading font-semibold mt-2" data-testid="quality-network-score">{data.network_score}</div>
            <div className={`mt-1 font-medium ${data.band === "excellent" || data.band === "good" ? "text-green-300" : data.band === "needs_attention" ? "text-amber-300" : "text-red-300"}`}>{BAND_LABEL[data.band]}</div>
          </CardContent>
        </Card>
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {["excellent", "good", "needs_attention", "critical"].map((b) => (
            <Card key={b} className="border-border" data-testid={`quality-band-${b}`}>
              <CardContent className="p-4">
                <div className={`text-3xl font-heading font-semibold ${BAND_STYLE[b]}`}>{data.bands?.[b] || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">{BAND_LABEL[b]}</div>
              </CardContent>
            </Card>
          ))}
          <Card className="border-border"><CardContent className="p-4"><div className="text-3xl font-heading font-semibold text-orange-700">{data.open_issues}</div><div className="text-xs text-muted-foreground mt-1">Unresolved</div></CardContent></Card>
          <Card className="border-border"><CardContent className="p-4"><div className="text-3xl font-heading font-semibold text-green-700">{data.resolved_issues}</div><div className="text-xs text-muted-foreground mt-1">Resolved</div></CardContent></Card>
          <Card className="border-border"><CardContent className="p-4"><div className="text-3xl font-heading font-semibold">{data.checks_count}</div><div className="text-xs text-muted-foreground mt-1">Live Alerts</div></CardContent></Card>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Location scorecards */}
          {data.locations?.length >= 1 && (
            <div>
              <h2 className="font-heading text-lg font-semibold mb-3">{data.locations.length > 1 ? "Locations" : "Location Scorecard"}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {data.locations.map((l) => (
                  <Card key={l.id} className="border-border" data-testid="quality-location-card">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div><div className="font-heading font-semibold">{l.name}</div><div className="text-xs text-muted-foreground">{l.city}</div></div>
                        <div className="text-right"><div className={`text-2xl font-heading font-semibold ${BAND_STYLE[l.band]}`}>{l.score}</div><StatusBadge value={l.band === "needs_attention" ? "needs_attention" : l.band} /></div>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {Object.entries(l.metrics).map(([k, v]) => (
                          <div key={k}>
                            <div className="flex justify-between text-[11px] text-muted-foreground"><span className="capitalize">{k.replace(/_/g, " ")}</span><span>{v}%</span></div>
                            <ProgressBar value={v} className="h-1" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Issues table */}
          <div>
            <h2 className="font-heading text-lg font-semibold mb-3">Quality Issues</h2>
            <Tabs defaultValue="open">
              <TabsList className="h-auto">
                {["open", "in_progress", "resolved"].map((s) => (
                  <TabsTrigger key={s} value={s} data-testid={`quality-tab-${s}`} className="capitalize">{s.replace("_", " ")} <span className="ml-1.5 text-xs text-muted-foreground">({byStatus(s).length})</span></TabsTrigger>
                ))}
              </TabsList>
              {["open", "in_progress", "resolved"].map((s) => (
                <TabsContent key={s} value={s} className="mt-3 space-y-2">
                  {byStatus(s).map((i) => (
                    <Card key={i.id} className="border-border hover:bg-muted/40 cursor-pointer transition-colors" onClick={() => navigate(`/quality/issues/${i.id}`)} data-testid="quality-issue-row">
                      <CardContent className="p-3.5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${SEV_STYLE[i.severity]}`}>{i.severity}</span>
                          <div>
                            <div className="text-sm font-medium">{i.title}</div>
                            <div className="text-xs text-muted-foreground capitalize">{i.type.replace(/_/g, " ")}{i.location_name ? ` · ${i.location_name}` : ""}</div>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-muted-foreground" />
                      </CardContent>
                    </Card>
                  ))}
                  {byStatus(s).length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No {s.replace("_", " ")} issues.</p>}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>

        {/* Sidebar: checklist + live alerts */}
        <div className="space-y-6">
          {data.checklist && (
            <Card className="border-border">
              <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ListChecks size={16} strokeWidth={1.5} /> Improvement Checklist</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.checklist.map((c) => (
                  <div key={c.label} className="flex items-center gap-2 text-sm" data-testid="quality-checklist-item">
                    {c.done ? <CheckCircle2 size={16} className="text-green-600" /> : <Circle size={16} className="text-muted-foreground/40" />}
                    <span className={c.done ? "" : "text-muted-foreground"}>{c.label}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TriangleAlert size={16} strokeWidth={1.5} /> Automated Checks</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.checks.map((c) => (
                <div key={c.title} className="rounded-lg border border-border/60 p-3" data-testid="quality-check-item">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className={c.severity === "high" ? "text-orange-600 mt-0.5" : c.severity === "medium" ? "text-amber-600 mt-0.5" : "text-muted-foreground mt-0.5"} />
                      <div><div className="text-sm font-medium">{c.title}</div><div className="text-xs text-muted-foreground capitalize">{c.type.replace(/_/g, " ")}</div></div>
                    </div>
                    {canManage && <Button size="sm" variant="ghost" className="h-7 text-xs shrink-0" onClick={() => generate(c)} data-testid="quality-generate-issue">Track</Button>}
                  </div>
                </div>
              ))}
              {data.checks.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">All clear — no active alerts.</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New Quality Issue</DialogTitle><DialogDescription>Log a quality or compliance issue to track and resolve.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="issue-field-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="issue-field-type" className="capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>{ISSUE_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid gap-1.5"><Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger data-testid="issue-field-severity" className="capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high", "critical"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid gap-1.5"><Label>Recommended action</Label><Textarea value={form.recommended_action} onChange={(e) => setForm({ ...form, recommended_action: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.title} data-testid="issue-save-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TrainerQuality({ data, issues, navigate }) {
  return (
    <div>
      <PageHeader title="Quality" subtitle="Quality items assigned to you and related to your classes." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ShieldCheck size={16} /> My Quality Issues</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {issues.map((i) => (
              <div key={i.id} className="rounded-lg border border-border/60 p-3 cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/quality/issues/${i.id}`)} data-testid="quality-issue-row">
                <div className="flex items-center gap-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${SEV_STYLE[i.severity]}`}>{i.severity}</span><span className="text-sm font-medium">{i.title}</span></div>
                <div className="text-xs text-muted-foreground mt-1 capitalize">{i.status.replace("_", " ")}</div>
              </div>
            ))}
            {issues.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No issues assigned to you.</p>}
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TriangleAlert size={16} /> Items To Fix</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.checks.map((c) => (
              <div key={c.title} className="rounded-lg border border-border/60 p-3" data-testid="quality-check-item">
                <div className="text-sm font-medium">{c.title}</div>
                <div className="text-xs text-muted-foreground capitalize">{c.type.replace(/_/g, " ")}</div>
              </div>
            ))}
            {data.checks.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nothing needs attention.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
